(function () {
  const CUSTOM_FIT_VALUE = 'custom fit';
  const OPTION_ROOT_SELECTOR = [
    '[class*="easify" i]',
    '[id*="easify" i]',
    '[data-app*="easify" i]',
    '[data-app-name*="easify" i]',
    '[class^="epo" i]',
    '[id^="epo" i]',
    '[class*=" epo" i]',
    '[id*=" epo" i]',
    '[class*="easify-options" i]',
    '[id*="easify-options" i]',
  ].join(',');
  const EMBROIDERY_LABELS = [
    'embroidery',
    'embroidery placement',
    'embroidery position',
    'embroidery content',
    'image upload',
  ];
  const PRODUCT_FORM_SELECTOR = 'form[is="product-form"], form[action*="/cart/add"]';
  const ADD_TO_CART_BUTTON_SELECTOR = 'button[type="submit"], input[type="submit"], button[name="add"], input[name="add"]';
  const BUY_IT_NOW_BUTTON_SELECTOR = '.shopify-payment-button__button, .shopify-payment-button button';
  const STANDARD_SIZE_GROUP = 'standard';

  let observer;
  let scheduled = false;

  function normalize(value) {
    return String(value || '').trim().toLowerCase().replace(/\s+/g, ' ');
  }

  function isSizeOptionName(name) {
    const normalized = normalize(name);
    return normalized.includes('size') || normalized.includes('尺码') || normalized.includes('尺寸');
  }

  function getProductScope(form) {
    return form?.closest('product-info') || document;
  }

  function getCustomFitSizePicker(scope = document) {
    const pickers = scope.querySelectorAll('variant-selects .product-form__input[data-option-name]');

    for (const picker of pickers) {
      if (!isSizeOptionName(picker.dataset.optionName)) continue;

      const hasCustomFitChoice = Array.from(picker.querySelectorAll('input, option')).some((choice) => {
        return (
          normalize(choice.value || choice.textContent).includes(CUSTOM_FIT_VALUE) ||
          normalize(choice.dataset.sizeChoice).includes('made-to-measure') ||
          normalize(choice.dataset.sizeGroup).includes('made-to-measure')
        );
      });

      if (hasCustomFitChoice) return picker;
    }

    return null;
  }

  function getSelectedSizeValue(scope = document) {
    const preferredPicker = getCustomFitSizePicker(scope);
    const pickers = preferredPicker
      ? [preferredPicker]
      : Array.from(scope.querySelectorAll('variant-selects .product-form__input[data-option-name]'));

    for (const picker of pickers) {
      if (!isSizeOptionName(picker.dataset.optionName)) continue;

      const select = picker.querySelector('select');
      if (select) return normalize(select.value);

      const checked = picker.querySelector('input[type="radio"]:checked:not([data-size-group-control])');
      if (checked) return normalize(checked.value);

      const selectedText = picker.querySelector('[data-selected-swatch-value]');
      if (selectedText) return normalize(selectedText.textContent);
    }

    return '';
  }

  function isStandardSizeMode(form) {
    const picker = getCustomFitSizePicker(getProductScope(form));
    if (!picker) return false;

    const checkedGroup = picker.querySelector('[data-size-group-control]:checked');
    const selectedGroup = normalize(checkedGroup?.dataset.sizeGroup || checkedGroup?.value || picker.dataset.activeSizeGroup);
    if (selectedGroup) return selectedGroup === STANDARD_SIZE_GROUP;

    const selectedSize = getSelectedSizeValue(getProductScope(form));
    return !!selectedSize && !selectedSize.includes(CUSTOM_FIT_VALUE);
  }

  function textMatchesEmbroidery(text) {
    const normalized = normalize(text);
    return EMBROIDERY_LABELS.some((label) => normalized.includes(label));
  }

  function findOptionRoot(element) {
    const pluginRoot = element.closest(OPTION_ROOT_SELECTOR);
    if (!pluginRoot) return null;
    if (pluginRoot.matches('product-info, variant-selects, form[is="product-form"]')) return null;
    if (pluginRoot.closest('variant-selects')) return null;

    return pluginRoot;
  }

  function getEmbroideryContainers() {
    const roots = new Set();

    document.querySelectorAll(OPTION_ROOT_SELECTOR).forEach((element) => {
      if (!element.matches('product-info, variant-selects') && textMatchesEmbroidery(element.textContent)) {
        roots.add(element);
      }
    });

    document.querySelectorAll('label, legend, strong, b, span, p').forEach((element) => {
      if (!textMatchesEmbroidery(element.textContent)) return;
      const root = findOptionRoot(element);
      if (root) roots.add(root);
    });

    return Array.from(roots).filter((element) => {
      const nestedRoot = Array.from(roots).some((root) => root !== element && root.contains(element));
      return !nestedRoot;
    });
  }

  function setDisabled(container, disabled) {
    container.querySelectorAll('input, select, textarea, button').forEach((control) => {
      if (disabled) {
        if (control.disabled) {
          control.dataset.customFitEmbroideryWasDisabled = 'true';
        }
        if (!control.disabled) control.disabled = true;
      } else if (control.dataset.customFitEmbroideryWasDisabled !== 'true') {
        if (control.disabled) control.disabled = false;
      }

      if (!disabled) {
        delete control.dataset.customFitEmbroideryWasDisabled;
      }
    });
  }

  function getSelectedVariantId(form) {
    const input = form?.querySelector('input[name="id"]');
    let variantId = input?.value || input?.getAttribute('value') || '';

    if (!variantId) {
      const selectedVariantJson = getProductScope(form)
        .querySelector('variant-selects [data-selected-variant]')
        ?.textContent;

      if (selectedVariantJson) {
        try {
          variantId = JSON.parse(selectedVariantJson)?.id?.toString() || '';
        } catch (error) {
          variantId = '';
        }
      }
    }

    if (variantId && input) {
      input.disabled = false;
      input.value = variantId;
      input.setAttribute('value', variantId);
    }

    return variantId;
  }

  function getQuantity(form) {
    const quantity = Number(form?.querySelector('[name="quantity"]')?.value || 1);
    return Number.isFinite(quantity) && quantity > 0 ? quantity : 1;
  }

  function removeCustomOptionData(formData) {
    const customOptionKeys = [];

    formData.forEach((value, key) => {
      const normalizedKey = normalize(key);
      if (
        key.indexOf('properties[') === 0 ||
        normalizedKey.includes('easify') ||
        normalizedKey.includes('tpo') ||
        normalizedKey.includes('fitsme')
      ) {
        customOptionKeys.push(key);
      }
    });

    customOptionKeys.forEach((key) => formData.delete(key));
  }

  function buildStandardSizeFormData(form) {
    const formData = new FormData(form);
    const variantId = getSelectedVariantId(form);

    removeCustomOptionData(formData);
    if (variantId) formData.set('id', variantId);
    if (!formData.get('quantity')) formData.set('quantity', getQuantity(form));

    return formData;
  }

  function getProductFormForButton(button) {
    const formId = button?.getAttribute('form');
    if (formId) {
      const form = document.getElementById(formId);
      if (form?.matches(PRODUCT_FORM_SELECTOR)) return form;
    }

    return button?.closest(PRODUCT_FORM_SELECTOR);
  }

  function getClickedCartButton(event) {
    const target = event.target;
    if (!target?.closest) return null;

    return target.closest(`${ADD_TO_CART_BUTTON_SELECTOR}, ${BUY_IT_NOW_BUTTON_SELECTOR}`);
  }

  function isBuyItNowButton(button) {
    return !!button?.matches(BUY_IT_NOW_BUTTON_SELECTOR);
  }

  function isAddToCartButton(button) {
    return !!button?.matches(ADD_TO_CART_BUTTON_SELECTOR);
  }

  function stopEasifyValidation(event) {
    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();
  }

  function displayVariantError(form) {
    const message = window.FoxTheme?.variantStrings?.unavailable || 'Please select a valid option.';
    if (typeof form?.displayFormErrors === 'function') {
      form.displayFormErrors(message);
    } else {
      window.alert(message);
    }
  }

  function submitStandardSizeAddToCart(form, button) {
    if (form.dataset.standardSizeSubmitting === 'true') return;
    if (button?.getAttribute('aria-disabled') === 'true') return;

    const variantId = getSelectedVariantId(form);
    if (!variantId) {
      displayVariantError(form);
      return;
    }

    if (!window.FoxTheme?.utils?.fetchConfig || !window.FoxTheme?.routes?.cart_add_url) {
      form.submit();
      return;
    }

    form.dataset.standardSizeSubmitting = 'true';

    if (typeof form.displayFormErrors === 'function') form.displayFormErrors();

    const sectionsToBundle = [];
    document.documentElement.dispatchEvent(
      new CustomEvent('cart:grouped-sections', { bubbles: true, detail: { sections: sectionsToBundle } })
    );

    const config = window.FoxTheme.utils.fetchConfig('javascript');
    config.headers['X-Requested-With'] = 'XMLHttpRequest';
    delete config.headers['Content-Type'];

    const formData = buildStandardSizeFormData(form);
    formData.append('sections', sectionsToBundle);
    formData.append('sections_url', window.location.pathname);

    config.body = formData;
    form.formData = formData;
    form.lastSubmittedElement = button || form;

    const submitButton = form.submitButtonElement || form.querySelector('[type="submit"]');
    submitButton?.setAttribute('aria-disabled', 'true');
    submitButton?.classList.add('btn--loading');

    if (typeof form.handleFormSubmission === 'function') {
      form.handleFormSubmission(config);
      window.setTimeout(() => {
        delete form.dataset.standardSizeSubmitting;
      }, 1500);
      return;
    }

    fetch(`${window.FoxTheme.routes.cart_add_url}`, config)
      .then((response) => response.json())
      .then((parsedState) => {
        if (parsedState.status) {
          displayVariantError(form);
          return;
        }

        document.dispatchEvent(
          new CustomEvent('cart:refresh', {
            bubbles: true,
            detail: {
              open: true,
            },
          })
        );
      })
      .catch((error) => console.log(error))
      .finally(() => {
        submitButton?.classList.remove('btn--loading');
        submitButton?.removeAttribute('aria-disabled');
        delete form.dataset.standardSizeSubmitting;
      });
  }

  function checkoutWithStandardSizeVariant(form) {
    const variantId = getSelectedVariantId(form);
    if (!variantId) {
      displayVariantError(form);
      return;
    }

    const root = window.Shopify?.routes?.root || '/';
    const rootWithSlash = root.endsWith('/') ? root : `${root}/`;
    window.location.href = `${rootWithSlash}cart/${encodeURIComponent(variantId)}:${encodeURIComponent(
      getQuantity(form)
    )}?checkout`;
  }

  function handleStandardSizeCartClick(event) {
    const button = getClickedCartButton(event);
    if (!button) return;

    const form = getProductFormForButton(button);
    if (!form || !isStandardSizeMode(form)) return;
    if (!isAddToCartButton(button) && !isBuyItNowButton(button)) return;

    stopEasifyValidation(event);

    if (isBuyItNowButton(button)) {
      checkoutWithStandardSizeVariant(form);
    } else {
      submitStandardSizeAddToCart(form, button);
    }
  }

  function handleStandardSizeFormSubmit(event) {
    const form = event.target;
    if (!form?.matches?.(PRODUCT_FORM_SELECTOR) || !isStandardSizeMode(form)) return;

    stopEasifyValidation(event);
    submitStandardSizeAddToCart(form, event.submitter || form.querySelector('[type="submit"]'));
  }

  function updateEmbroideryVisibility() {
    scheduled = false;
    observer.disconnect();

    const showEmbroidery = getSelectedSizeValue().includes(CUSTOM_FIT_VALUE);
    getEmbroideryContainers().forEach((container) => {
      if (container.hidden === showEmbroidery) container.hidden = !showEmbroidery;

      const ariaHidden = showEmbroidery ? 'false' : 'true';
      if (container.getAttribute('aria-hidden') !== ariaHidden) {
        container.setAttribute('aria-hidden', ariaHidden);
      }

      container.classList.toggle('custom-fit-embroidery-hidden', !showEmbroidery);
      setDisabled(container, !showEmbroidery);
    });

    observer.observe(document.documentElement, { childList: true, subtree: true });
  }

  function scheduleUpdate() {
    if (scheduled) return;
    scheduled = true;
    window.requestAnimationFrame(updateEmbroideryVisibility);
  }

  document.addEventListener('change', (event) => {
    if (event.target.closest('variant-selects')) scheduleUpdate();
  });
  window.addEventListener('click', handleStandardSizeCartClick, { capture: true });
  window.addEventListener('submit', handleStandardSizeFormSubmit, { capture: true });
  document.addEventListener('variant:changed', scheduleUpdate);
  document.addEventListener('shopify:section:load', scheduleUpdate);
  document.addEventListener('DOMContentLoaded', scheduleUpdate);

  observer = new MutationObserver(scheduleUpdate);
  observer.observe(document.documentElement, { childList: true, subtree: true });

  scheduleUpdate();
})();
